/**
 * "New comment" from the LIVE EDITING SURFACE — the one path that works on both
 * the continuous DOM editor and the paginated `<canvas>`.
 *
 * ── THE DEFECT THIS EXISTS TO KILL ───────────────────────────────────────────
 * `ribbon-cmd-review.new-comment` was a DEAD BUTTON on the paginated surface.
 * Its catalog entry (`requires: 'comment'`) enables on `Boolean(host.addComment)`
 * — always true — so the button rendered live, and the workbench's `addComment`
 * then read `window.getSelection()` and early-returned. A `<canvas>` document has
 * no DOM selection: the reviewer's selection lives in the WASM editor core as an
 * `{anchor,focus}` IR coordinate pair, and `window.getSelection()` there is
 * PERMANENTLY collapsed however much text is painted highlighted on screen. So the
 * one gesture the product exists for — anchor a comment to a quote — did nothing
 * at all, silently, on the surface that is now the default editing view.
 *
 * ── WHY IT LIVES HERE AND ASKS THE HANDLE ────────────────────────────────────
 * Only a surface knows its own selection. That is already the contract behind
 * `ElaborateEditorHandle.hasSelection()`; this module is the same rule applied to
 * anchoring, via the surface-agnostic `buildSelector()` (no `Range` argument =
 * "your own live selection") and `selectionViewportRect()`. Both are upstream in
 * `@norkron/web-elaborate-editor`, so the DOM controller and the canvas surface
 * answer the SAME question and can never diverge again. Nothing here re-derives
 * anchoring geometry — the durable W3C selector (exact quote + affixes +
 * block-relative UTF-8 byte offsets) is the lib's, and `quote_hash` is the
 * product's own wire field (brief §6: exact quote_hash ⇒ confidence 1.0).
 */

import type { AnchorSelector, ElaborateEditorHandle } from "@norkron/web-elaborate-editor";
import type { AnnotationDraft } from "../api/types";
import { hashQuote } from "./anchoring";

/**
 * The optional half of the surface contract this module needs and
 * `ElaborateEditorHandle` does not yet declare: only a surface knows which BLOCK
 * KINDS its selection covers, and therefore whether the range can be re-found in
 * the stored section body. A surface that cannot judge simply omits it (the
 * verdict then rests on the selector alone).
 */
export interface AnchorableSurface {
  anchorRefusal?(): string | null;
}

/** Everything the composer needs to open over a live surface selection. */
export interface SurfaceCommentTarget {
  /** The wire draft posted to `POST /documents/:id/comments` — the DURABLE
   *  anchor row (`el.comment_anchors`), which is what survives a reload. */
  readonly draft: AnnotationDraft;
  /** The same geometry in the lib's shape, kept so the host can hand it straight
   *  back to `handle.resolveAnchor(selector, commentId)` and paint the band once
   *  the server has minted the comment id. `null` when the surface could not name
   *  the range at all (an unaddressable block) — the composer still opens. */
  readonly selector: AnchorSelector | null;
  /** Viewport rect of the selection (composer placement), or `null` when the
   *  surface cannot report one — the caller then places the composer itself
   *  rather than parking it at the origin. */
  readonly rect: DOMRect | null;
  /** Whether this comment will be able to re-find itself after the document is
   *  edited. `false` is NOT a refusal to comment — it is a disclosure. */
  readonly anchorable: boolean;
  /** Human-readable, shown IN the composer when `!anchorable`. Never silent, and
   *  never a toast that replaces the popup. */
  readonly reason: string | null;
}

/**
 * Resolve the mounted editing surface's LIVE selection into a comment target.
 *
 * ⚠ `null` MEANS "THERE IS NO SELECTION", AND NOTHING ELSE. It used to also mean
 * "there is a selection but I don't like it" — `!selector || !selector.exactQuote`
 * — and `ArticleWorkbench` turned that into a toast telling the reviewer to select
 * text they had already selected. The owner's requirement is explicit
 * (2026-07-29): "the comment popup must show regardless of the selection". So a
 * range that exists but cannot be anchored durably comes back as a target with
 * `anchorable: false` and a CONCRETE reason, and the composer opens over it.
 *
 * Works identically on the DOM editor and the paginated canvas: the surface is
 * asked, never the DOM.
 */
export function commentTargetFromSurface(
  handle: (ElaborateEditorHandle & AnchorableSurface) | null | undefined,
): SurfaceCommentTarget | null {
  if (!handle) return null;
  const selector = handle.buildSelector();
  // No selector AND no ranged selection = a bare caret. The one honest null.
  if (!selector && !(handle.hasSelection?.() ?? false)) return null;
  const rect = handle.selectionViewportRect?.() ?? null;
  const refusal = surfaceRefusal(handle, selector);
  if (!refusal && selector) {
    return { draft: draftFromSelector(selector), selector, rect, anchorable: true, reason: null };
  }
  return {
    draft: selector ? draftFromSelector(selector) : emptyDraft(),
    selector,
    rect,
    anchorable: false,
    reason: refusal ?? UNNAMEABLE,
  };
}

const UNNAMEABLE =
  "This selection can't be named durably — one of its blocks has no id, so a comment on it could not be found again.";

/** The surface's own verdict when it has one, else the selector-only fallback. */
function surfaceRefusal(
  handle: ElaborateEditorHandle & AnchorableSurface,
  selector: AnchorSelector | null,
): string | null {
  const fromSurface = handle.anchorRefusal?.();
  if (fromSurface) return fromSurface;
  if (!selector) return UNNAMEABLE;
  // A surface that does not implement `anchorRefusal` still must not claim an
  // empty quote is anchorable: there is nothing to search the body for.
  if (fromSurface === undefined && selector.exactQuote.trim().length === 0) {
    return "The selected range contains no text to quote, so the comment can't re-find itself after an edit.";
  }
  return null;
}

/** A draft for a range the surface could not name. Posting it still records the
 *  reviewer's comment; it simply carries no durable anchor — which is exactly
 *  what {@link SurfaceCommentTarget.reason} tells them. */
function emptyDraft(): AnnotationDraft {
  return {
    block_ref: "",
    quote: "",
    prefix: "",
    suffix: "",
    start: 0,
    end: 0,
    quote_hash: hashQuote(""),
  };
}

/**
 * The durable selector → the product's wire draft. `quote_hash` is the brief's
 * exact-match confidence key; every other field is the lib's geometry verbatim.
 *
 * ── THE PERSISTED (start, end) PAIR IS BLOCK-RELATIVE TO `block_ref` ──────────
 * `AnchorSelector.start`/`end` are deliberately offsets into DIFFERENT blocks for
 * a cross-block range: `blockId`/`start` name one end, `endBlockId`/`end` the
 * other. `AnnotationDraft` has no second block id — `el.annotations` stores
 * `start_offset`/`end_offset` beside ONE `block_ref` (`src/db/comments_repo.zig`),
 * and every reader of that row takes both as offsets into `block_ref`'s text.
 *
 * Posting the raw pair therefore persists a nonsense row: `end_offset` indexes a
 * different block than `start_offset`, so `block_ref.text[start..end]` yields
 * garbage or runs past the end. It is invisible today only because the DURABLE
 * anchor is recomputed server-side by an exact `indexOf` of the quote and this
 * pair is discarded — invisible is not harmless.
 *
 * THE CONTRACT, chosen explicitly: the persisted pair stays BLOCK-RELATIVE to
 * `block_ref` and merely UNDER-DESCRIBES the range — it names the FIRST block's
 * slice (`blocks[0].end`, the offset at which the range left that block). The true
 * extent is carried by the QUOTE, which is what actually re-locates the anchor,
 * and client-side by `endBlockId`/`blocks[]`. The alternative — keep the raw pair
 * and teach every reader to be cross-block-aware — makes a legacy reader WRONG
 * until it is migrated; this one makes it correct-but-narrow, and only one of
 * those two can mislead.
 */
export function draftFromSelector(selector: AnchorSelector): AnnotationDraft {
  const firstBlock = selector.blocks?.[0];
  const end =
    firstBlock && selector.endBlockId != null && selector.endBlockId !== selector.blockId
      ? firstBlock.end
      : selector.end;
  return {
    block_ref: selector.blockId,
    quote: selector.exactQuote,
    prefix: selector.prefix,
    suffix: selector.suffix,
    start: selector.start,
    end,
    quote_hash: hashQuote(selector.exactQuote),
  };
}

/** The wire draft → the lib's selector shape, for handing a stored anchor back to
 *  `handle.resolveAnchor` after a reload. The inverse of {@link draftFromSelector}
 *  (`quote_hash` is derivable, so nothing is lost in the round trip). */
export function selectorFromDraft(
  draft: AnnotationDraft,
  baseVersionId?: string,
): AnchorSelector {
  return {
    blockId: draft.block_ref,
    exactQuote: draft.quote,
    prefix: draft.prefix,
    suffix: draft.suffix,
    start: draft.start,
    end: draft.end,
    ...(baseVersionId ? { baseVersionId } : {}),
  };
}
